/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { ReactNode, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Stack,
    TabPanel,
    Tabs,
    useBreakpoint,
} from 'sarsaparilla';
import PassTypesTable from './PassTypesTable';
import { PassType, PassTypeOptionType } from '../../types/PassType';

interface PassSelectionTabsProps {
    selectedPassTypeId?: string;
    passTypes: PassType[];
    passTypesRaw: PassType[];
    passTypesOptions?: PassTypeOptionType[];
    status?: string;
    isLoading?: boolean;
    renderPricing: () => ReactNode;
    onSelectPass: (id: string) => void;
    onRemovePass: (id: string) => void;
    handleViewHelperClick?: () => void;
    handleBackToStart?: () => void;
}

export default function PassSelectionTabs({
    selectedPassTypeId,
    passTypes,
    passTypesRaw,
    passTypesOptions,
    status,
    renderPricing,
    onSelectPass,
    onRemovePass,
    isLoading,
    handleViewHelperClick,
    handleBackToStart,
}: PassSelectionTabsProps) {
    const breakpoint = useBreakpoint();

    const buttonActions = (() => {
        const backButton = (
            <Button appearance="tertiary" onClick={handleBackToStart}>
                Back
            </Button>
        );

        const helpMeChooseButton = passTypesOptions?.length ? (
            <Button
                appearance="primary"
                isDisabled={isLoading}
                onClick={handleViewHelperClick}
                gaTrackingId="340878007548"
            >
                Help Me Choose a Pass
            </Button>
        ) : null;

        return (
            <ButtonGroup align="right" className="mt-2">
                {breakpoint?.isMdAndUp ? (
                    <>
                        {backButton}
                        {helpMeChooseButton}
                    </>
                ) : (
                    <>
                        {helpMeChooseButton}
                        {backButton}
                    </>
                )}
            </ButtonGroup>
        );
    })();

    const filteredStandardPassTypes = useMemo(
        () => passTypes.filter((item) => !item?.features?.['is-commercial-pass']),
        [passTypes]
    );
    const filteredStandardRawPassTypes = useMemo(
        () => passTypesRaw.filter((item) => !item?.features?.['is-commercial-pass']),
        [passTypesRaw]
    );

    const renderStandardPassTypes = (
        <>
            <Box>
                <Stack>
                    <PassTypesTable
                        selectedPassTypeId={selectedPassTypeId}
                        passTypes={filteredStandardPassTypes}
                        passTypesRaw={filteredStandardRawPassTypes}
                        status={status}
                        showDescription={false}
                        pricing={renderPricing()}
                        onSelectPass={onSelectPass}
                        onRemovePass={onRemovePass}
                    />
                </Stack>
            </Box>
            {buttonActions}
        </>
    );

    const filteredCommercialPassTypes = useMemo(
        () => passTypes.filter((item) => item?.features?.['is-commercial-pass']),
        [passTypes]
    );
    const filteredCommercialRawPassTypes = useMemo(
        () => passTypesRaw.filter((item) => item?.features?.['is-commercial-pass']),
        [passTypesRaw]
    );

    return !isEmpty(filteredCommercialRawPassTypes) ? (
        <Tabs stretchTabsToFit={false}>
            <TabPanel label="Standard Passes">{renderStandardPassTypes}</TabPanel>
            <TabPanel label="Commercial Passes">
                <Stack>
                    <Alert
                        heading="Information for Commercial Users"
                        type="info"
                        hasAriaLive
                    >
                        <ul>
                            <li>
                                Commercial users must be logged in to their{' '}
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={process.env.SITE_ROOT}
                                >
                                    {process.env.SITE_NAME}
                                </a>{' '}
                                account to purchase a commercial Site Pass.
                            </li>
                            <li>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://help.recreation.gov/helpcenter/en/coming-soon?id=kb_article&sysparm_article=KB0012321&table=kb_knowledge&searchTerm=coming%20soon`}
                                >
                                    Learn more
                                </a>{' '}
                                about pass options and rules for commercial users.
                            </li>
                            <li>
                                Commercial tour operators that need to purchase a
                                Nonresident ATB pass can purchase that pass{' '}
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`${process.env.SITE_ROOT}/interagency-pass/types/nonresident`}
                                >
                                    here.
                                </a>
                            </li>
                        </ul>
                    </Alert>
                    <PassTypesTable
                        selectedPassTypeId={selectedPassTypeId}
                        passTypes={filteredCommercialPassTypes}
                        passTypesRaw={filteredCommercialRawPassTypes}
                        status={status}
                        showDescription={false}
                        pricing={renderPricing()}
                        onSelectPass={onSelectPass}
                        onRemovePass={onRemovePass}
                    />
                </Stack>
            </TabPanel>
        </Tabs>
    ) : (
        renderStandardPassTypes
    );
}
